import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "./App.css";
import Icon from "./components/Icon";
import FeedbackDrawer from "./components/FeedbackDrawer";
import FeedbackAdmin from "./pages/FeedbackAdmin";
import ItemLabelOpen from "./pages/ItemLabelOpen";
import AdPage from "./pages/AdPage";
import { AppleIcon, PlayIcon } from "./components/StoreBadges";
import { QRCodeSVG } from "qrcode.react";

const APP_STORE_URL = "https://apps.apple.com/us/app/pahincho/id6788609680";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.pahincho.pahincho";

const DownloadBlock: React.FC = () => (
  <section className="home-panel download-block">
    <h2 className="download-block__title">Download app</h2>
    <p className="download-block__lead">
      Mobile app only — get Pahincho from the App Store or Google Play.
    </p>

    <div className="download-block__cta">
      <div className="download-block__badges">
        <a
          className="store-pill"
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <AppleIcon />
          <span>iOS</span>
        </a>
        <a
          className="store-pill"
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <PlayIcon />
          <span>Android</span>
        </a>
      </div>

      <div className="store-qr">
        <QRCodeSVG value={APP_STORE_URL} size={112} level="M" marginSize={2} />
        <span>iOS</span>
      </div>

      <div className="store-qr">
        <QRCodeSVG value={PLAY_STORE_URL} size={112} level="M" marginSize={2} />
        <span>Android</span>
      </div>
    </div>
  </section>
);

const MainPage: React.FC = () => (
  <div className="App">
    {/* Header */}
    <header className="header">
      <div className="header-content">
        <div className="logo-section" aria-hidden="true">
          <Icon name="pahincho-logo" className="header-logo" />
        </div>
        <div className="company-name">
          <h1>Pahincho</h1>
        </div>
        <nav className="navigation" aria-label="Main">
          <a href="/about.html">About</a>
          <a href="/howitworks.html">How it Works</a>
          <a href="/contact.html">Contact</a>
        </nav>
      </div>
    </header>

    {/* Main Content */}
    <main className="main-content">
      <div className="home-stack">
        {/* Row 1: 3-slide animation — full width */}
        <section className="home-panel home-slides">
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
            }}
            pagination={{
              clickable: true,
            }}
            loop={true}
            className="homeSwiper"
          >
            <SwiperSlide>
              <div className="slide">
                <div className="slide-image-container">
                  <img
                    src="/images/onboarding_screen1-5.png"
                    alt="Give What You Don't Need"
                  />
                </div>
                <div className="slide-content">
                  <h2>Give What You Don't Need</h2>
                  <p>
                    Pass on what you dont need. Someone nearby will love it.
                  </p>
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="slide">
                <div className="slide-image-container">
                  <img
                    src="/images/onboarding_screen2-3.png"
                    alt="Borrow Without Buying"
                  />
                </div>
                <div className="slide-content">
                  <h2>Borrow Without Buying</h2>
                  <p>
                    Borrow from a neighbor instead of buying. Save money, help
                    the planet
                  </p>
                </div>
              </div>
            </SwiperSlide>
            <SwiperSlide>
              <div className="slide">
                <div className="slide-image-container">
                  <img
                    src="/images/onboarding_screen3-1.png"
                    alt="Share, Earn Pinchos, Repeat"
                  />
                </div>
                <div className="slide-content">
                  <h2>Share, Earn Pinchos, Repeat</h2>
                  <p>
                    Give more, earn more. Your Pinchos unlock anything nearby
                  </p>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </section>

        <div className="home-divider" role="separator" aria-hidden="true" />

        <DownloadBlock />
      </div>
    </main>

    {/* Footer */}
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-social">
          <a
            href="https://www.facebook.com/people/Pahincho-LLC/61582677628288/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="footer-social__icon"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
          </a>
          <a
            href="https://www.youtube.com/@PahinchoLLC"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
            className="footer-social__icon"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
              <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#3a3a3a" />
            </svg>
          </a>
        </div>

        <nav className="footer-legal-links" aria-label="Legal">
          <a href="/privacy-policy.html">Privacy Policy</a>
          <span className="footer-legal-sep" aria-hidden="true">
            |
          </span>
          <a href="/terms-of-service.html">Terms of Service</a>
        </nav>

        <p className="footer-copy">
          &copy; {new Date().getFullYear()} Pahincho. All rights reserved.
        </p>
      </div>
    </footer>

    {/* Feedback drawer — rendered outside normal flow, fixed to viewport */}
    <FeedbackDrawer />
  </div>
);

const App: React.FC = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<MainPage />} />
      <Route path="/ad" element={<AdPage />} />
      <Route path="/feedback" element={<FeedbackAdmin />} />
      <Route path="/q/:code" element={<ItemLabelOpen />} />
    </Routes>
  </BrowserRouter>
);

export default App;
